import { useId } from 'react';

type XWingProps = {
  className?: string;
};

export function XWing({ className }: XWingProps) {
  const uid = useId().replace(/:/g, '');
  const hullId = `${uid}-hull`;
  const foilId = `${uid}-foil`;
  const engineId = `${uid}-engine`;
  const glassId = `${uid}-glass`;

  return (
    <svg className={className} viewBox="0 0 720 340" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id={hullId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#5b6874" />
          <stop offset="45%" stopColor="#d2dae0" />
          <stop offset="100%" stopColor="#77838e" />
        </linearGradient>
        <linearGradient id={foilId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d7dee4" />
          <stop offset="52%" stopColor="#8995a0" />
          <stop offset="100%" stopColor="#3c4853" />
        </linearGradient>
        <radialGradient id={engineId} cx="36%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#8ec6ff" />
          <stop offset="55%" stopColor="#1c5cff" />
          <stop offset="100%" stopColor="#152d54" />
        </radialGradient>
        <radialGradient id={glassId} cx="38%" cy="28%" r="72%">
          <stop offset="0%" stopColor="#9ad2ff" />
          <stop offset="48%" stopColor="#365b76" />
          <stop offset="100%" stopColor="#101b24" />
        </radialGradient>
      </defs>

      {/* Four independently opened S-foils form the unmistakable X silhouette. */}
      <path d="M330 158 270 42 232 46 276 169Z" fill={`url(#${foilId})`} opacity="0.78" />
      <path d="M330 182 270 298 232 294 276 171Z" fill={`url(#${foilId})`} opacity="0.78" />
      <path d="M352 157 335 12 293 15 293 169Z" fill={`url(#${foilId})`} />
      <path d="M352 183 335 328 293 325 293 171Z" fill={`url(#${foilId})`} />
      <path
        d="M330 158 270 42 232 46 276 169ZM330 182 270 298 232 294 276 171ZM352 157 335 12 293 15 293 169ZM352 183 335 328 293 325 293 171Z"
        stroke="rgba(16,32,51,0.34)"
        strokeWidth="1.6"
      />

      {/* Rebel identification panels and foil mechanics. */}
      <path
        d="m255 73 18 35-15 8-18-36ZM255 267l18-35-15-8-18 36ZM309 53l5 43-16 2-4-43ZM309 287l5-43-16-2-4 43Z"
        fill="#9c463b"
        opacity="0.82"
      />
      <path
        d="M286 119 270 42M286 221l-16 77M307 104l28-92M307 236l28 92"
        stroke="rgba(238,243,248,0.18)"
        strokeWidth="2"
      />

      {/* Four full-length Taim & Bak laser cannons project forward from the foil tips. */}
      <g stroke="#9aa6b2" strokeWidth="1">
        <path d="M250 44h194" stroke="#26313b" strokeWidth="7" />
        <path d="M250 296h194" stroke="#26313b" strokeWidth="7" />
        <path d="M314 14h148" stroke="#26313b" strokeWidth="7" />
        <path d="M314 326h148" stroke="#26313b" strokeWidth="7" />
        <path d="M270 44h153M270 296h153M334 14h108M334 326h108" />
      </g>
      <g fill="#303b45" stroke="#a8b4be" strokeWidth="1.5">
        <circle cx="250" cy="44" r="10" />
        <circle cx="250" cy="296" r="10" />
        <circle cx="314" cy="14" r="10" />
        <circle cx="314" cy="326" r="10" />
      </g>
      <g fill="#c4001a">
        <circle className="xwing__muzzle xwing__muzzle--one" cx="444" cy="44" r="5" />
        <circle className="xwing__muzzle xwing__muzzle--two" cx="444" cy="296" r="5" />
        <circle className="xwing__muzzle xwing__muzzle--three" cx="462" cy="14" r="5" />
        <circle className="xwing__muzzle xwing__muzzle--four" cx="462" cy="326" r="5" />
      </g>

      {/* Four cylindrical Incom engines at the foil roots. */}
      <g>
        <path d="M230 114h116l24 24-24 18H230Z" fill="#707d88" stroke="#2b3640" strokeWidth="2" />
        <ellipse cx="230" cy="135" rx="24" ry="22" fill="#303b45" />
        <ellipse cx="230" cy="135" rx="13" ry="12" fill={`url(#${engineId})`} />
        <path d="M230 184h116l24 18-24 24H230Z" fill="#5d6a75" stroke="#2b3640" strokeWidth="2" />
        <ellipse cx="230" cy="205" rx="24" ry="22" fill="#303b45" />
        <ellipse cx="230" cy="205" rx="13" ry="12" fill={`url(#${engineId})`} />
        <path d="M270 87h99l26 33-31 16h-94Z" fill="#8a96a1" stroke="#2b3640" strokeWidth="2" />
        <ellipse cx="270" cy="110" rx="22" ry="20" fill="#303b45" />
        <ellipse cx="270" cy="110" rx="11" ry="10" fill={`url(#${engineId})`} />
        <path d="M270 204h94l31 16-26 33h-99Z" fill="#67747f" stroke="#2b3640" strokeWidth="2" />
        <ellipse cx="270" cy="230" rx="22" ry="20" fill="#303b45" />
        <ellipse cx="270" cy="230" rx="11" ry="10" fill={`url(#${engineId})`} />
      </g>

      {/* Long wedge fuselage and needle nose. */}
      <path d="M214 152 351 140 675 170 351 200 214 188Z" fill={`url(#${hullId})`} />
      <path d="M214 152 351 140 675 170 351 200 214 188Z" stroke="rgba(16,32,51,0.42)" strokeWidth="2" />
      <path d="M214 152v36" stroke="#2b3640" strokeWidth="4" />
      <path d="m454 153 221 17-221 17 41-17Z" fill="#56636e" />
      <path d="M351 140v60M405 148v44M538 160v20" stroke="rgba(238,243,248,0.17)" strokeWidth="1.3" />
      <path d="M594 164 675 170l-81 6Z" fill="#c5cdd4" opacity="0.62" />

      {/* Canopy and the astromech socket immediately behind it. */}
      <path d="m374 145 50 7 25 18-25 18-50 7-20-25Z" fill={`url(#${glassId})`} stroke="#a8b4be" strokeWidth="2" />
      <path d="m387 151 5 38m20-35 5 32m-42-16h70" stroke="rgba(220,235,245,0.45)" strokeWidth="1.2" />
      <ellipse cx="330" cy="159" rx="18" ry="10" fill="#29343e" />
      <path d="M317 159a13 13 0 0 1 26 0" fill="#d8e0e6" />
      <path d="M319 157a11 11 0 0 1 22 0" fill="#245db8" />
      <rect x="322" y="153" width="7" height="5" fill="#d8e0e6" />
      <circle cx="337" cy="151" r="2.3" fill="#c4001a" />

      {/* Mechanical seams stay subtle, matching the dark-side craft. */}
      <path d="M240 150v40M270 147v46M302 147v46M468 155v30" stroke="rgba(16,32,51,0.3)" strokeWidth="1.5" />
      <path d="M225 169h36M286 163h28M486 166h45" stroke="rgba(238,243,248,0.2)" strokeWidth="2" />

      {/* Alternating cannon pairs throw energized red bolts across the viewport. */}
      <g className="xwing__shot xwing__shot--one">
        <path d="M448 44h92" stroke="#ff1738" strokeWidth="13" strokeLinecap="round" opacity="0.48" />
        <path d="M448 44h92" stroke="#fff1f3" strokeWidth="3" strokeLinecap="round" />
      </g>
      <g className="xwing__shot xwing__shot--two">
        <path d="M448 296h92" stroke="#ff1738" strokeWidth="13" strokeLinecap="round" opacity="0.48" />
        <path d="M448 296h92" stroke="#fff1f3" strokeWidth="3" strokeLinecap="round" />
      </g>
      <g className="xwing__shot xwing__shot--three">
        <path d="M466 14h92" stroke="#ff1738" strokeWidth="13" strokeLinecap="round" opacity="0.48" />
        <path d="M466 14h92" stroke="#fff1f3" strokeWidth="3" strokeLinecap="round" />
      </g>
      <g className="xwing__shot xwing__shot--four">
        <path d="M466 326h92" stroke="#ff1738" strokeWidth="13" strokeLinecap="round" opacity="0.48" />
        <path d="M466 326h92" stroke="#fff1f3" strokeWidth="3" strokeLinecap="round" />
      </g>
      <g className="xwing__arcs" stroke="#ff3550" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path className="xwing__arc xwing__arc--one" d="m420 35 9 7 8-10 10 10 10-7" />
        <path className="xwing__arc xwing__arc--two" d="m420 305 9-7 8 10 10-10 10 7" />
        <path className="xwing__arc xwing__arc--three" d="m438 25 9-7 8 10 10-10 10 7" />
        <path className="xwing__arc xwing__arc--four" d="m438 315 9 7 8-10 10 10 10-7" />
      </g>
    </svg>
  );
}
